import { useCallback, useEffect, useState } from "react";

import {
  makeGetApiCall,
  makePostApiCall,
} from "../../shared/makeApiCall";
import {
  buildFormValuesFromTenant,
  buildTenantPayload,
} from "../../shared/tenantSettingsForm";
import { DEFAULT_MODULES } from "../../shared/tenantModules";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import { generateUniqueNumber } from "../../shared/utility";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";

export function CompanySettingsViewModel(
  addSnackbar,
  logout,
  updateTenantSuccess,
) {
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [initialValues, setInitialValues] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const getErrorMessage = (callResult, fallback) =>
    callResult.data?.message ||
    callResult.data?.subtitle ||
    fallback;

  const handleCallFailure = (
    callResult,
    { title, defaultMessage, forbiddenMessage },
  ) => {
    if (callResult.errorStatus === 401) {
      logout();
      return;
    }

    if (callResult.errorStatus === 403 && forbiddenMessage) {
      showErrorModal("Access denied", forbiddenMessage);
      return;
    }

    showErrorModal(title, getErrorMessage(callResult, defaultMessage));
  };

  const hydrateForm = useCallback((tenant) => {
    const values = buildFormValuesFromTenant(tenant);
    const mergedModules = {
      ...DEFAULT_MODULES,
      ...values.modules,
    };

    const hydrated = {
      ...values,
      modules: mergedModules,
    };

    setFormValues(hydrated);
    setInitialValues(hydrated);
    setLogoPreview(null);
    setLogoFile(null);
  }, []);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    const callResult = await makeGetApiCall("tenant/settings");

    setLoading(false);
    if (callResult.success) {
      hydrateForm(callResult.data.tenant);
      return;
    }

    handleCallFailure(callResult, {
      title: "Could not load settings",
      defaultMessage: "We could not load your company settings. Please try again.",
      forbiddenMessage:
        "You do not have permission to view company settings.",
    });
  }, [hydrateForm, logout]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateField = (name, value) => {
    setFormValues((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const updateModule = (key, enabled) => {
    setFormValues((current) => ({
      ...current,
      modules: {
        ...current.modules,
        [key]: enabled,
      },
    }));
  };

  const updateLabel = (key, value) => {
    setFormValues((current) => ({
      ...current,
      labels: {
        ...current.labels,
        [key]: value,
      },
    }));
  };

  const resetChanges = () => {
    if (initialValues) {
      setFormValues(initialValues);
      setLogoPreview(null);
      setLogoFile(null);
    }
  };

  const handleLogoSelect = (file) => {
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadLogo = async () => {
    if (!logoFile) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Select a logo file first.",
          SnackbarType.Error,
        ),
      );
      return;
    }

    const formData = new FormData();
    formData.append("logo", logoFile);

    setUploadingLogo(true);
    const callResult = await makePostApiCall(
      "tenant/logo",
      formData,
      "multipart/form-data",
    );
    setUploadingLogo(false);

    if (callResult.success) {
      const logoUrl = callResult.data.logo_url;
      setFormValues((current) => ({
        ...current,
        logo_url: logoUrl,
      }));
      setInitialValues((current) =>
        current
          ? {
              ...current,
              logo_url: logoUrl,
            }
          : current,
      );
      setLogoFile(null);
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Logo uploaded successfully.",
          SnackbarType.Success,
        ),
      );
      return;
    }

    handleCallFailure(callResult, {
      title: "Logo upload failed",
      defaultMessage: "We could not upload the logo.",
      forbiddenMessage:
        "You do not have permission to update company settings.",
    });
  };

  const saveSettings = async (validatedValues) => {
    const payload = buildTenantPayload(validatedValues);

    setSaving(true);
    const callResult = await makePostApiCall("tenant/settings", payload);
    setSaving(false);

    if (callResult.success) {
      const tenant = callResult.data.tenant;
      hydrateForm(tenant);
      updateTenantSuccess(tenant);
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message || "Company settings updated successfully.",
          SnackbarType.Success,
        ),
      );
      return;
    }

    handleCallFailure(callResult, {
      title: "Save failed",
      defaultMessage: "We could not save your company settings.",
      forbiddenMessage:
        "You do not have permission to update company settings.",
    });
  };

  return {
    emptyModal,
    setEmptyModelOpen,
    loading,
    saving,
    uploadingLogo,
    formValues,
    logoPreview,
    logoFile,
    fetchSettings,
    updateField,
    updateModule,
    updateLabel,
    resetChanges,
    handleLogoSelect,
    uploadLogo,
    saveSettings,
  };
}
